// Formulario de crear/editar una regla de asignación, en modal. La validación local evita quemar un
// request, pero NO reemplaza al lambda: si él rechaza, su error se muestra tal cual.
import { useId, useState } from 'react';
import type { PriorityWeights } from '@fiado/type-kit/bin/loanCollectorAssignment/dtos/PriorityWeights';
import { AssignmentStrategyEnum } from '@fiado/type-kit/bin/loanCollectorAssignment/enums/AssignmentStrategyEnum';
import { Button, Input, Modal, NumberInput, Select, Switch } from '@/kit';
import { userMessageOf } from '@/api/errors';
import {
    areWeightsValid,
    describeStrategy,
    hasWeights,
    parseMaxLoad,
    weightsSum,
    WEIGHTS_TOTAL,
} from '../logic';
import type { RuleRow } from '../types';
import { WEIGHT_LABEL } from './RuleDetail';

const STRATEGY_OPTIONS = Object.values(AssignmentStrategyEnum).map((strategy) => ({
    value: strategy,
    label: describeStrategy(strategy),
}));

const FIELD_LABEL = 'text-[10px] font-bold uppercase tracking-[0.04em] text-muted-foreground';

const DEFAULT_WEIGHTS: PriorityWeights = { storeMatch: 50, loadAvailable: 30, seniority: 20 };

/** Lo que se dice cuando la regla guardada no traía pesos y el formulario los precargó. */
export const PRELOADED_WEIGHTS_NOTE =
    'Esta regla no traía los pesos del ranking; se precargaron 100 repartidos. Ajústalos antes de guardar.';

/** El tope que el lambda acepta es un entero ≥ 1; vaciar el campo sigue siendo «sin tope». */
const MAX_LOAD_INVALID = 'El tope tiene que ser un número entero de 1 o más, o quedar vacío.';

/** Copia los pesos cambiando uno. `PriorityWeights` es una clase del type-kit: no se hace spread. */
function withWeight(
    weights: PriorityWeights,
    key: keyof PriorityWeights,
    weight: number,
): PriorityWeights {
    return {
        storeMatch: key === 'storeMatch' ? weight : weights.storeMatch,
        loadAvailable: key === 'loadAvailable' ? weight : weights.loadAvailable,
        seniority: key === 'seniority' ? weight : weights.seniority,
    };
}

/** Lo que el formulario devuelve; la pantalla lo traduce al request de crear o editar. */
export interface RuleFormValues {
    strategy: AssignmentStrategyEnum;
    /** `null` = sin tope. El campo se puede vaciar a propósito. */
    maxLoadPerCollector: number | null;
    priorityWeights: PriorityWeights;
    active: boolean;
}

/** El estado interno: el tope vive como texto para distinguir «vacío» de «0» o «2.5». */
interface RuleFormState {
    strategy: AssignmentStrategyEnum;
    maxLoadText: string;
    priorityWeights: PriorityWeights;
    active: boolean;
}

function initialState(rule: RuleRow | null, draft: RuleFormValues | undefined): RuleFormState {
    const values: RuleFormValues = draft ?? {
        strategy: rule?.strategy ?? AssignmentStrategyEnum.BY_STORE,
        maxLoadPerCollector: rule?.maxLoadPerCollector ?? null,
        // Una regla sin pesos arranca en los defaults y el formulario lo DICE: no finge leerlos.
        priorityWeights: hasWeights(rule?.priorityWeights)
            ? {
                  storeMatch: rule?.priorityWeights.storeMatch ?? 0,
                  loadAvailable: rule?.priorityWeights.loadAvailable ?? 0,
                  seniority: rule?.priorityWeights.seniority ?? 0,
              }
            : DEFAULT_WEIGHTS,
        active: rule?.active ?? false,
    };
    return {
        strategy: values.strategy,
        maxLoadText: values.maxLoadPerCollector === null ? '' : String(values.maxLoadPerCollector),
        priorityWeights: values.priorityWeights,
        active: values.active,
    };
}

export interface RuleFormProps {
    open: boolean;
    /** `null` = crear una regla nueva; con regla = editarla. */
    rule: RuleRow | null;
    /** Lo ya tecleado, cuando se vuelve al formulario tras cancelar la confirmación. */
    draft?: RuleFormValues;
    isSaving: boolean;
    /** El error que devolvió el lambda al guardar, si lo hubo. */
    error: Error | null;
    onCancel: () => void;
    onSubmit: (values: RuleFormValues) => void;
}

export function RuleForm({
    open,
    rule,
    draft,
    isSaving,
    error,
    onCancel,
    onSubmit,
}: RuleFormProps) {
    const fieldId = useId();
    const [state, setState] = useState<RuleFormState>(() => initialState(rule, draft));

    const sum = weightsSum(state.priorityWeights);
    const weightsValid = areWeightsValid(state.priorityWeights);
    const maxLoad = parseMaxLoad(state.maxLoadText);
    // Editar una regla que el lambda guardó sin pesos: los que se ven son precargados, no los suyos.
    const weightsPreloaded =
        draft === undefined && rule !== null && !hasWeights(rule.priorityWeights);

    const patchWeight = (key: keyof PriorityWeights, weight: number) => {
        setState((current) => ({
            ...current,
            priorityWeights: withWeight(current.priorityWeights, key, weight),
        }));
    };

    return (
        <Modal
            isOpen={open}
            onClose={onCancel}
            title={rule ? 'Editar la regla de asignación' : 'Nueva regla de asignación'}
            description="Con estos criterios el motor elige a qué cobrador va cada crédito nuevo."
        >
            <div className="space-y-3">
                <Select
                    id={`${fieldId}-strategy`}
                    label="Estrategia"
                    value={state.strategy}
                    options={STRATEGY_OPTIONS}
                    onChange={(value) => {
                        setState((current) => ({
                            ...current,
                            strategy: value as AssignmentStrategyEnum,
                        }));
                    }}
                />

                <Input
                    id={`${fieldId}-max`}
                    type="number"
                    min={1}
                    step={1}
                    label="Tope por cobrador"
                    hint="Vacío = sin tope. El motor deja de asignarle a quien llegue a este número."
                    error={maxLoad.valid ? undefined : MAX_LOAD_INVALID}
                    value={state.maxLoadText}
                    onChange={(event) => {
                        const raw = event.target.value;
                        setState((current) => ({ ...current, maxLoadText: raw }));
                    }}
                />

                <div className="space-y-1.5">
                    <span className={FIELD_LABEL}>Pesos del ranking</span>
                    {weightsPreloaded ? (
                        <p role="status" className="text-[11px] text-warning">
                            {PRELOADED_WEIGHTS_NOTE}
                        </p>
                    ) : null}
                    {(Object.keys(WEIGHT_LABEL) as (keyof PriorityWeights)[]).map((key) => (
                        <div key={key} className="flex items-center justify-between gap-2">
                            <label className="text-[12px]" htmlFor={`${fieldId}-${key}`}>
                                {WEIGHT_LABEL[key]}
                            </label>
                            <NumberInput
                                id={`${fieldId}-${key}`}
                                className="w-24"
                                min={0}
                                max={WEIGHTS_TOTAL}
                                value={state.priorityWeights[key]}
                                onValueChange={(weight) => {
                                    patchWeight(key, weight);
                                }}
                            />
                        </div>
                    ))}
                    <p
                        className={
                            weightsValid
                                ? 'text-[11px] text-muted-foreground'
                                : 'text-[11px] text-destructive'
                        }
                    >
                        Suma: <strong className="font-bold">{sum ?? 0}</strong> de {WEIGHTS_TOTAL}
                        {weightsValid
                            ? ''
                            : ` · ajusta los pesos hasta que sumen ${String(WEIGHTS_TOTAL)}, es lo que el motor exige.`}
                    </p>
                </div>

                <div className="flex items-center justify-between gap-2">
                    <label className="text-[12px]" htmlFor={`${fieldId}-active`}>
                        Activar esta regla al guardar
                    </label>
                    <Switch
                        aria-label="Activar esta regla al guardar"
                        checked={state.active}
                        onCheckedChange={(checked) => {
                            setState((current) => ({ ...current, active: checked }));
                        }}
                    />
                </div>

                {error === null ? null : (
                    <p role="alert" className="text-[11.5px] text-destructive">
                        {userMessageOf(error)}
                    </p>
                )}

                <div className="flex justify-end gap-2 pt-1">
                    <Button variant="outline" onClick={onCancel} disabled={isSaving}>
                        Cancelar
                    </Button>
                    <Button
                        disabled={!weightsValid || !maxLoad.valid}
                        isLoading={isSaving}
                        onClick={() => {
                            onSubmit({
                                strategy: state.strategy,
                                maxLoadPerCollector: maxLoad.value,
                                priorityWeights: state.priorityWeights,
                                active: state.active,
                            });
                        }}
                    >
                        Guardar
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
